import { useContext, useState, useRef, useEffect } from "react";
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Link,
  Button,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Container,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ContactlessOutlinedIcon from "@mui/icons-material/ContactlessOutlined";
import DialpadOutlinedIcon from "@mui/icons-material/DialpadOutlined";
import { UserContext } from "../../context/UserContext";
import StripePaymentElement from "../../common/component/StripePaymentElement";
import FinixPaymentElement from "../../common/component/FinixPaymentElement";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentStatus from "./PaymentStatus";
import PaymentSuccessScreen from "./PaymentSuccessScreen";
import currency from "currency.js";
import bkstApi from "../../api";
import CollectBalancePayment from "../../order/component/CollectBalancePayment";
import Option, { OPTION_GRID_TILE_SX } from "../../product/component/Option";
import FulfillmentTab from "../component/fulfillment/FulfillmentTab";
import CustomerTab from "../../customer/component/CustomerTab";
import {
  formatPhoneNumber,
  validateFulfillment,
  validateCustomer,
  getDefaultQuoteExpiration,
  buildPaymentForOrderApi,
  buildInvoiceQuoteFulfillment,
  getOtherPosPaymentMethods,
  POS_OTHER_PAYMENT_UI_KEY,
  isSalesTaxEnabled,
  isCustomerEmpty,
} from "../util";
import AddDeposit from "../component/AddDeposit";
import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import PaymentEntry from "../../order/component/PaymentEntry";
import SameDayPaymentDueWarning from "../../order/component/SameDayPaymentDueWarning";
import CashEntry from "../../order/component/CashEntry";
import PaymentMethodIcon from "../component/PaymentMethodIcon";

const key = process.env.REACT_APP_STRIPE_KEY;
const promise = loadStripe(key);

const OFFLINE_PAYMENT_METHODS = {
  cash: { title: "Cash", label: "CASH PAYMENT" },
};

const isOfflinePaymentMethod = (method) => method in OFFLINE_PAYMENT_METHODS;

const PAYMENT_OPTION_ICON_SX = { color: "#777" };

const PAYMENT_OPTION_ICONS = {
  reader: <ContactlessOutlinedIcon sx={PAYMENT_OPTION_ICON_SX} />,
  keyed: <DialpadOutlinedIcon sx={PAYMENT_OPTION_ICON_SX} />,
  cash: <PaymentMethodIcon paymentType="offline" />,
  invoice: <ReceiptOutlinedIcon sx={PAYMENT_OPTION_ICON_SX} />,
  [POS_OTHER_PAYMENT_UI_KEY]: <OpenInNewOutlinedIcon sx={PAYMENT_OPTION_ICON_SX} />,
};

const PAYMENT_METHOD_OPTIONS = [
  { key: "reader", title: "Card Reader" },
  { key: "keyed", title: "Virtual Terminal" },
  { key: "cash", title: "Cash" },
  { key: "invoice", title: "Invoice" },
  { key: POS_OTHER_PAYMENT_UI_KEY, title: "Other" },
];

export default function AddPaymentMethod(props) {
  const { user } = useContext(UserContext);
  const errorRef = useRef(null);

  const { items, reqId } = props;

  const [tab, setTab] = useState(props.fulfillment ? "fulfillment" : "payment");

  const [customer, setCustomer] = useState(props.customer || "");
  const [fulfillment, setFulfillment] = useState(
    props.fulfillment ? { date: "", time: "", location: "", ...props.fulfillment } : ""
  );

  const [paymentMethod, setPaymentMethod] = useState(
    user.config?.readerId ? "reader" : "keyed"
  );
  const [payment, setPayment] = useState("");
  const [collectSalesTax, setCollectSalesTax] = useState(
    props.collectSalesTax !== undefined ? props.collectSalesTax : isSalesTaxEnabled(user.config)
  );

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [status, setStatus] = useState("");

  const [result, setResult] = useState("");

  const [acceptDeposit, setAcceptDeposit] = useState(false);
  const [deposit, setDeposit] = useState("");
  const [dueDate, setDueDate] = useState("");

  const [cash, setCash] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [otherPaymentMethod, setOtherPaymentMethod] = useState("");
  const [takePaymentOpen, setTakePaymentOpen] = useState(false);

  const otherPaymentMethods = getOtherPosPaymentMethods(user.config);
  // Actual method recorded on the order ("ebt", "check", "cash"…) rather than the "other" tile key.
  const resolvedPaymentMethod =
    paymentMethod === POS_OTHER_PAYMENT_UI_KEY ? otherPaymentMethod : paymentMethod;

  useEffect(() => {
    if (fulfillment?.date) {
      setAcceptDeposit(false);
      setDeposit("");
      setDueDate("");
    }
  }, [fulfillment?.date]);

  const remainingDueAfterSale = (data = {}) => {
    if (data.quoteId) {
      return 0;
    }
    return Math.max(0, +currency(payment?.total || 0).subtract(payment?.due || 0));
  };

  const buildSuccessResult = (data = {}) => ({
    ...data,
    email: customer?.email,
    phone: customer?.phone,
    cash: paymentMethod === "cash" ? cash : "",
    remainingDue: remainingDueAfterSale(data),
    fulfillmentType: fulfillment?.type || "",
  });

  const finishCheckout = (data = {}) => {
    setResult(buildSuccessResult(data));
    setStatus("success");
    clearSale();
  };

  const openTakePayment = () => {
    if (!result.orderId && !result.paymentIntentId) {
      return;
    }
    setTakePaymentOpen(true);
    setStatus("");
    setOpen(false);
  };

  const scrollPosToTop = () => {
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const validate = () => {
    let arr = [];
    arr = validateFulfillment(fulfillment);
    if (customer || fulfillment.type === "pickup" || fulfillment.type === "delivery") {
      arr = arr.concat(validateCustomer(customer));
    }
    return arr;
  };

  const handleSubmit = (fn) => {
    let arr = validate();
    setErrors(arr);
    if (arr.length > 0) {
      errorRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    } else {
      setStatus("processing");

      fn()
        .then((res) => finishCheckout(res.data))
        .catch((e) => {
          setStatus("fail");
          setErrors([`Error: ${e.response?.data?.message}`]);
        });
    }
  };

  const handleClose = () => {
    const saleFinished = status === "success";
    setOpen(false);
    setStatus("");
    setResult("");
    setErrors([]);
    if (saleFinished) {
      props.onSaleDismissed?.();
    }
  };

  const handleTakePaymentOpenChange = (isOpen) => {
    setTakePaymentOpen(isOpen);
    if (isOpen) {
      return;
    }

    const restoreSuccess = () => {
      setStatus("success");
      setOpen(true);
    };

    const orderId = result.orderId;
    if (!orderId && !result.paymentIntentId) {
      restoreSuccess();
      return;
    }

    const loadRemaining = (id) =>
      bkstApi(`/my/order/${id}`).then((res) => {
        const due = +res.data.payment?.due || 0;
        const fulfillmentType = res.data.fulfillment?.type || result.fulfillmentType || "";

        // Paid in full — CollectBalancePayment already showed success; don't show it again
        if (due <= 0) {
          handleClose();
          return;
        }

        setResult((prev) => ({
          ...prev,
          orderId: res.data.id,
          remainingDue: due,
          fulfillmentType,
          email: res.data.customer?.email || prev.email,
          phone: res.data.customer?.phone || prev.phone,
        }));
        restoreSuccess();
      });

    const resolveIdWithPoll = () => {
      if (orderId) {
        return Promise.resolve(orderId);
      }

      const deadline = Date.now() + 15000;
      const poll = () =>
        bkstApi(`/my/payment-intent/${result.paymentIntentId}/order`)
          .then((res) => res.data.orderId)
          .catch((e) => {
            if (e.response?.status !== 404 || Date.now() > deadline) {
              throw e;
            }
            return new Promise((resolve) => setTimeout(resolve, 1000)).then(poll);
          });

      return poll();
    };

    resolveIdWithPoll()
      .then((id) => loadRemaining(id))
      .catch(() => {
        // Keep prior success/balance-due UI — sale already completed
        restoreSuccess();
      });
  };

  const clearSale = () => {
    setFulfillment("");
    setCustomer("");
    setCash("");
    setReferenceId("");
    setOtherPaymentMethod("");
    setTab("payment");
    setErrors([]);
    setPaymentMethod(user.config?.readerId ? "reader" : "keyed");

    localStorage.removeItem("cart");
    props.onSaleComplete?.();
  };

  const handleTabChange = (val) => {
    let arr = [];
    if (tab === "fulfillment") {
      arr = validateFulfillment(fulfillment);
    } else if (tab === "customer" && val === "payment") {
      if (fulfillment.type === "togo" && isCustomerEmpty(customer)) {
        setCustomer("");
        setTab(val);
        setErrors([]);
        return;
      }
      arr = validateCustomer(customer);
    }
    setErrors(arr);
    if (arr.length > 0) {
      errorRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    } else {
      setTab(val);
    }
  };

  const chargePaymentMethod = (paymentMethodIdOrToken) => {
    const payload = {
      payment: buildPaymentForOrderApi(payment, dueDate),
      dueDate,
      items,
      fulfillment,
      customer,
      reqId,
      settings: { collectSalesTax },
    };

    const paymentUrl =
      user.processor === "finix" ? `/my/finix/payment` : `/my/payment`;
    if (user.processor === "finix") {
      payload.token = paymentMethodIdOrToken;
      payload.pmCode = "moto";
      payload.amount = payment.due;
    } else {
      payload.paymentMethodId = paymentMethodIdOrToken;
    }

    return bkstApi.post(paymentUrl, payload);
  };

  const sendToCardReader = () => {
    const payload = {
      payment: buildPaymentForOrderApi(payment, dueDate),

      dueDate,
      items,
      fulfillment,
      customer,
      reqId,
      settings: { collectSalesTax },
    };

    return bkstApi.post(`/my/reader/${user.config.readerId}`, payload);
  };

  const createOfflineOrder = () => {
    const payload = {
      payment: buildPaymentForOrderApi(payment, dueDate),
      paymentMethod: resolvedPaymentMethod,
      dueDate,
      items,
      fulfillment,
      customer,
      reqId,
      receiptPrinterId: user.config?.receiptPrinterId || "",
      settings: { collectSalesTax },
    };

    if (referenceId.trim()) {
      payload.referenceId = referenceId.trim();
    }
    if (paymentMethod === "cash" && cash?.tendered != null && cash.tendered !== "") {
      payload.tendered = cash.tendered;
    }

    return bkstApi.post(`/my/order`, payload);
  };

  const selectPaymentMethod = (key) => {
    setPaymentMethod(key);
    if (key !== POS_OTHER_PAYMENT_UI_KEY) {
      setOtherPaymentMethod("");
      setReferenceId("");
    }
  };

  const sendInvoice = () => {
    const payload = {
      items,
      fulfillment: buildInvoiceQuoteFulfillment(fulfillment),
      customer,
      settings: { collectSalesTax },
      exdate: getDefaultQuoteExpiration(fulfillment.date),
      mode: "send",
    };
    if (+payment?.discount > 0) {
      if (!payload.adjustments) {
        payload.adjustments = {};
      }
      payload.adjustments.discount = {
        description: "Merchant Discount",
        value: +payment?.discount,
        type: "manual",
      };
    }

    return bkstApi.post(`/my/quote`, payload);
  };

  const paymentSummary = (
    <Paper elevation={0}>
      <Box p={1}>
        <PaymentEntry
          items={items}
          fulfillment={fulfillment}
          payment={payment}
          acceptDeposit={acceptDeposit}
          deposit={deposit}
          adjustments={props.adjustments}
          paymentMethod={resolvedPaymentMethod}
          collectSalesTax={collectSalesTax}
          onCollectSalesTaxChange={setCollectSalesTax}
          onChange={(o) => {
            setPayment(o);
          }}
        />
        <AddDeposit
          paymentSummary={payment}
          dueDate={dueDate}
          fulfillment={fulfillment}
          customerType={customer?.type}
          onChange={(o) => {
            setDeposit(o.deposit);
            setDueDate(o.dueDate);
            setAcceptDeposit(true);
          }}
        />
      </Box>
    </Paper>
  );

  const paymentMethodContent = (
    <Box>
      <Box mb={2} sx={{ display: "flex", gap: 1 }}>
        {PAYMENT_METHOD_OPTIONS.map(({ key, title }) => (
          <Box key={key} sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Option
              title={title}
              titleIcon={PAYMENT_OPTION_ICONS[key]}
              sx={OPTION_GRID_TILE_SX}
              onClick={() => selectPaymentMethod(key)}
              selected={paymentMethod === key}
            />
          </Box>
        ))}
      </Box>
      <SameDayPaymentDueWarning
        dueDate={dueDate}
        fulfillmentDate={fulfillment?.date}
        customerType={customer?.type}
      />
      <Box>
        {paymentMethod === "reader" && (
          <Box>
            <Box mb={2}>
              <PaymentStatus
                sx={{ fontSize: "1.5rem", padding: "1.5rem" }}
                amount={payment.due}
                onSubmit={() => {
                  let arr = validate();
                  setErrors(arr);
                  if (arr.length > 0) {
                    errorRef.current?.scrollIntoView({
                      behavior: "smooth",
                    });
                    return;
                  } else {
                    return sendToCardReader();
                  }
                }}
                onSuccess={(o) => {
                  finishCheckout(o);
                }}
                readerId={user.config.readerId}
              />
            </Box>
            {user.config?.readerId ? (
              <Box textAlign={"center"}>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  Click the button to send a payment request to the card reader. Ask customer to
                  verify amount & tap or insert card into the card reader to complete payment.
                </Typography>
              </Box>
            ) : (
              <Box textAlign={"center"}>
                <Typography variant="body1" color="error">
                  You do not have a Card Reader to accept in-store payment. Please contact
                  BakeStreet using the Online Chat to purchase a Card Reader.
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {paymentMethod === "keyed" && (
          <Box>
            <Box mb={2}>
              {user.processor === "finix" ? (
                <FinixPaymentElement
                  formElementId="finix-pos-payment-form"
                  validate={validate}
                  chargePaymentMethod={(token) => {
                    handleSubmit(() => chargePaymentMethod(token));
                  }}
                  amount={payment.due}
                />
              ) : (
                <Elements stripe={promise}>
                  <StripePaymentElement
                    sx={{ fontSize: "1.5rem", padding: "1.5rem" }}
                    validate={validate}
                    chargePaymentMethod={(paymentMethodId) => {
                      handleSubmit(() => chargePaymentMethod(paymentMethodId));
                    }}
                    amount={payment.due}
                    name={customer.name}
                    phone={customer.phone}
                  />
                </Elements>
              )}
            </Box>
            <Box my={2} textAlign={"center"}>
              <Typography color="textSecondary" variant="body1" gutterBottom>
                Virtual Terminal turns your device into a credit card terminal. Collect payments
                over phone or in-person by entering the customer's card details below.
              </Typography>
            </Box>
          </Box>
        )}
        {isOfflinePaymentMethod(paymentMethod) && (
          <Box>
            {paymentMethod === "cash" && (
              <Paper elevation={0} sx={{ backgroundColor: "#F7F7F7" }}>
                <CashEntry
                  amountDue={currency(payment.due).intValue}
                  onChange={(o) => setCash(o)}
                />
              </Paper>
            )}
            <Box mb={2}>
              <Button
                sx={{ fontSize: "1.5rem", padding: "1.5rem" }}
                color="primary"
                variant="contained"
                onClick={() => handleSubmit(createOfflineOrder)}
                fullWidth
                disabled={
                  +payment.due > 0 &&
                  paymentMethod === "cash" &&
                  (!cash || +cash?.tendered < +payment.due)
                }
              >
                {currency(payment.due).format()} | {OFFLINE_PAYMENT_METHODS[paymentMethod].label}
              </Button>
            </Box>
          </Box>
        )}
        {paymentMethod === POS_OTHER_PAYMENT_UI_KEY && (
          <Box>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1,
                p: 2,
                mb: 2,
                bgcolor: "#fff",
              }}
            >
              <Typography variant="overline" color="textSecondary" display="block" gutterBottom>
                Other payment type
              </Typography>
              <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                <RadioGroup
                  value={otherPaymentMethod}
                  onChange={(e) => setOtherPaymentMethod(e.target.value)}
                >
                  {Object.entries(otherPaymentMethods)
                    .sort(([, a], [, b]) => a.title.localeCompare(b.title))
                    .map(([key, { title }]) => (
                      <FormControlLabel
                        key={key}
                        value={key}
                        control={<Radio />}
                        label={<Typography variant="body1">{title}</Typography>}
                      />
                    ))}
                </RadioGroup>
              </FormControl>
              <TextField
                fullWidth
                label="Reference ID (optional)"
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                helperText=""
              />
            </Paper>
            <Box mb={2}>
              <Button
                sx={{ fontSize: "1.5rem", padding: "1.5rem" }}
                color="primary"
                variant="contained"
                onClick={() => handleSubmit(createOfflineOrder)}
                fullWidth
                disabled={!otherPaymentMethod}
              >
                {currency(payment.due).format()} |{" "}
                {otherPaymentMethods[otherPaymentMethod]?.label || "RECORD PAYMENT"}
              </Button>
            </Box>
          </Box>
        )}
        {paymentMethod === "invoice" && (
          <Box>
            <Box mb={2}>
              <Button
                color="primary"
                variant="contained"
                onClick={() => handleSubmit(() => sendInvoice())}
                fullWidth
                disabled={!customer}
                sx={{ fontSize: "1.5rem", padding: "1.5rem" }}
              >
                SEND INVOICE
              </Button>
              <Box mt={0.5}>
                {!customer && (
                  <Box textAlign={"center"}>
                    <Typography color="error" variant="body2">
                      Customer details like name, phone are required to send an invoice.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box my={2} textAlign={"center"}>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                Customer will be notified via text & email. The order will be confirmed when the
                customer pays for the order.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {fulfillment ? (
        <Button
          fullWidth
          color="primary"
          variant="contained"
          onClick={() => {
            setOpen(true);
          }}
          disabled={props.disabled}
          sx={{ height: "75px", fontSize: "1.5rem" }}
        >
          CONTINUE
        </Button>
      ) : (
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            fullWidth
            color="secondary"
            variant="contained"
            onClick={() => {
              scrollPosToTop();
              setTab("fulfillment");
              setOpen(true);
            }}
            disabled={props.disabled}
            sx={{ height: "75px", fontSize: "1.5rem" }}
          >
            FUTURE
          </Button>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            onClick={() => {
              scrollPosToTop();
              setFulfillment({
                type: "togo",
                date: moment().format("YYYY-MM-DD"),
                time: moment().format("ha"),
                address: user.config.storeLocation[0].address,
                location: {
                  ...user.config.storeLocation[0].address,
                  id: user.config.storeLocation[0].id,
                },
                storeLocationId: user.config.storeLocation[0].id,
              });
              setTab("payment");
              setOpen(true);
            }}
            disabled={props.disabled}
            sx={{ height: "75px", fontSize: "1.5rem" }}
          >
            TO-GO
          </Button>
        </Box>
      )}
      {status !== "success" && (
        <Dialog fullScreen={true} open={open} scroll="paper" onClose={handleClose}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              margin: 0,
              backgroundColor: "#DEDEDE",
              padding: 0,
            }}
          >
            <Box sx={{ display: "flex" }}>
              <IconButton onClick={handleClose}>
                <ArrowBackIcon />
              </IconButton>
            </Box>
            <Box>
              <Tabs value={tab}>
                <Tab
                  value="fulfillment"
                  label="Fulfillment"
                  icon={<EventOutlinedIcon />}
                  iconPosition="start"
                  onClick={() => handleTabChange("fulfillment")}
                  sx={{ padding: "16px 16px", minHeight: 48 }}
                />
                <Tab
                  value="customer"
                  label="Customer"
                  icon={<PersonOutlineOutlinedIcon />}
                  iconPosition="start"
                  onClick={() => handleTabChange("customer")}
                  sx={{ padding: "24px", minHeight: 48 }}
                />
                <Tab
                  value="payment"
                  label="Payment"
                  icon={<PaymentsOutlinedIcon />}
                  iconPosition="start"
                  onClick={() => handleTabChange("payment")}
                  sx={{ padding: "24px", minHeight: 48 }}
                />
              </Tabs>
            </Box>
            {/* <Box mr={1}>
             <Typography variant="h3">{currency(paymentSummary.due).format()}</Typography>
           </Box> */}
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: "#F7F7F7" }}>
            <Box ref={errorRef}>
              {errors.length > 0 && (
                <Alert sx={{ justifyContent: "center" }} severity="error">
                  {errors.join(". ")}
                </Alert>
              )}
            </Box>
            <Container maxWidth="xl">
              <Box py={2}>
                <Box>
                  <Grid container spacing={4}>
                    <Grid item sm={4} xs={12}>
                      <Box>
                        {fulfillment.type && tab !== "fulfillment" && (
                          <Box mb={2}>
                            <Link
                              underline="none"
                              onClick={(e) => {
                                setTab("fulfillment");
                              }}
                            >
                              <Paper elevation={0}>
                                <Box p={1}>
                                  <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
                                    {fulfillment.type}
                                  </Typography>
                                  {(fulfillment.type === "pickup" ||
                                    fulfillment.type === "delivery") && (
                                    <Box>
                                      <Typography
                                        color="textSecondary"
                                        variant="overline"
                                        display={"block"}
                                      >
                                        {moment(fulfillment.date).format("ddd, MMM D, YYYY")} [
                                        {fulfillment.time || ""}]
                                      </Typography>
                                      <Typography color="textSecondary" variant="overline">
                                        {fulfillment.location.street}, {fulfillment.location.city},{" "}
                                        {fulfillment.location.state} {fulfillment.location.zip}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Paper>
                            </Link>
                          </Box>
                        )}

                        {tab === "payment" && (
                          <Box mb={2}>
                            {customer ? (
                              <Box>
                                <Link
                                  underline="none"
                                  onClick={(e) => {
                                    setTab("customer");
                                  }}
                                >
                                  <Paper elevation={0}>
                                    <Box p={1}>
                                      <Typography variant="h6">
                                        {customer.firstName} {customer.lastName}
                                      </Typography>
                                      <Typography variant="overline">
                                        {formatPhoneNumber(customer.phone)}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                </Link>
                              </Box>
                            ) : (
                              <Paper elevation={0}>
                                <Button
                                  startIcon={<AddIcon />}
                                  color={"primary"}
                                  onClick={(e) => {
                                    setTab("customer");
                                  }}
                                >
                                  Customer
                                </Button>
                              </Paper>
                            )}
                          </Box>
                        )}
                        {paymentSummary}
                      </Box>
                    </Grid>

                    {tab === "payment" ? (
                      <Grid item sm={8} xs={12}>
                        {paymentMethodContent}
                      </Grid>
                    ) : (
                      <Grid item sm={8} xs={12}>
                        {tab === "fulfillment" && (
                          <FulfillmentTab
                            fulfillment={fulfillment}
                            onChange={(o) => {
                              setFulfillment(o);
                            }}
                            onSubmit={() => handleTabChange("customer")}
                          />
                        )}
                        {tab === "customer" && (
                          <CustomerTab
                            customer={customer}
                            onChange={(o) => setCustomer(o)}
                            onSubmit={() => handleTabChange("payment")}
                          />
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Box>
            </Container>
            {status === "processing" && (
              <Backdrop open={true} style={{ zIndex: "1000", color: "red" }}>
                <CircularProgress color="inherit" />
              </Backdrop>
            )}
          </DialogContent>
        </Dialog>
      )}

      {status === "success" && (
        <PaymentSuccessScreen
          open={open}
          onClose={handleClose}
          result={result}
          newSaleTo={props.returnUrl || `/pos`}
          onCollectPayment={
            result.fulfillmentType === "togo" &&
            +result.remainingDue > 0 &&
            (result.orderId || result.paymentIntentId)
              ? openTakePayment
              : undefined
          }
        />
      )}

      {takePaymentOpen && (
        <CollectBalancePayment
          orderId={result.orderId}
          paymentIntentId={result.paymentIntentId}
          hideTrigger
          open={takePaymentOpen}
          onOpenChange={handleTakePaymentOpenChange}
          onOrderLoaded={(orderId) => setResult((prev) => ({ ...prev, orderId }))}
        />
      )}
    </>
  );
}
